import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { StatusPill, Detail } from "./shared";
import VendorEditForm from "./VendorEditForm";
import VendorReviewsManager from "./VendorReviewsManager";
import { VendorMediaItem, VendorMediaLightbox } from "./VendorMedia";
import AdminVendorMediaManager from "./AdminVendorMediaManager";
import { devLog } from "@/lib/log";

const VendorDetailDrawer = ({ app: listRow, onClose, onUpdated }) => {
  const [lightboxItem, setLightboxItem] = useState(null);
  const [editing, setEditing] = useState(false);
  // The drawer hydrates the slim list row with the full record on open
  // (admin list endpoint now returns just the table fields — projection
  // + pagination, see backend `VENDOR_LIST_PROJECTION`).
  const [full, setFull] = useState(listRow);
  useEffect(() => {
    setLightboxItem(null);
    setEditing(false);
    setFull(listRow);
    if (!listRow?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/vendor-applications/${listRow.id}`);
        if (!cancelled) setFull(r.data);
      } catch (e) {
        // Keep the slim record visible if the detail fetch fails.
        devLog("vendor detail fetch failed:", e?.message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Refetch only when the drawer target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRow?.id]);
  if (!listRow) return null;
  const app = full || listRow;
  const mediaUrls = Array.isArray(app.media_urls) ? app.media_urls : [];
  const orientation = app.media_orientation || "portrait";

  const saveEdits = async (patch) => {
    try {
      const r = await api.patch(
        `/admin/vendor-applications/${app.id}`,
        patch
      );
      setFull(r.data);
      onUpdated?.(r.data);
      toast("Vendor updated", { duration: 1800 });
      setEditing(false);
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not save", {
        duration: 2400,
      });
    }
  };

  return (
    <div
      role="dialog"
      data-testid="vendor-detail-drawer"
      className="fixed inset-0 z-50 flex"
    >
      <div
        className="flex-1 bg-[var(--ld-ink)]/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="w-full max-w-xl bg-[var(--ld-ivory)] overflow-y-auto p-8 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
              Vendor application
            </span>
            <h3 className="font-serif-display text-2xl mt-2 text-[var(--ld-ink)]">
              {app.company_name}
            </h3>
            <div className="mt-2"><StatusPill status={app.status} /></div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] p-1"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        <div className="mt-3">
          {editing ? null : (
            <button
              type="button"
              data-testid="vendor-edit-toggle"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-[var(--ld-ink)]/40 px-3 py-1.5 hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)] transition-colors"
            >
              Edit details
            </button>
          )}
        </div>

        {editing && (
          <VendorEditForm
            app={app}
            onSave={saveEdits}
            onCancel={() => setEditing(false)}
          />
        )}

        {/* Portfolio media */}
        {editing ? (
          <AdminVendorMediaManager
            appId={app.id}
            mediaUrls={mediaUrls}
            orientation={orientation}
            onUpdated={(newUrls) => {
              const next = { ...app, media_urls: newUrls };
              setFull(next);
              onUpdated?.(next);
            }}
          />
        ) : (
          mediaUrls.length > 0 && (
            <div className="mt-6" data-testid="vendor-media-strip">
              <div className="flex items-center justify-between mb-3">
                <p className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                  Portfolio · {mediaUrls.length}{" "}
                  {mediaUrls.length === 1 ? "file" : "files"} · {orientation}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {mediaUrls.map((u) => (
                  <VendorMediaItem
                    key={u}
                    url={u}
                    orientation={orientation}
                    onOpen={(url, isVideo) =>
                      setLightboxItem({ url, isVideo })
                    }
                  />
                ))}
              </div>
            </div>
          )
        )}

        <dl className="mt-6">
          <Detail label="Full name" value={app.full_name} />
          <Detail label="Email" value={app.email} />
          <Detail label="Contact" value={app.contact_number} />
          <Detail label="Services" value={app.service_types} />
          <Detail label="Based in" value={app.based_in} />
          <Detail label="Coverage" value={app.coverage} />
          <Detail label="Weddings completed" value={app.weddings_completed} />
          <Detail label="Pricing structure" value={app.pricing_structure} />
          <Detail label="Starting price" value={app.starting_price} />
          <Detail label="Packages" value={app.packages_addons} />
          <Detail label="Insurance" value={app.public_liability_insurance} />
          <Detail label="Business" value={app.business_registration} />
          <Detail label="Intro" value={app.short_intro} />
          <Detail label="Accuracy" value={app.accuracy_confirmed} />
          <Detail label="Responsibility" value={app.responsibility_agreed} />
          <Detail label="Public display" value={app.public_display_consent} />
          <Detail label="Content rights" value={app.content_rights_confirmed} />
          <Detail label="Marketing" value={app.marketing_consent} />
          <Detail label="UK compliance" value={app.uk_compliance_confirmed} />
          {app.other_details && (
            <Detail label="Other" value={app.other_details} />
          )}
          <Detail
            label="Submitted"
            value={
              app.submitted_at
                ? new Date(app.submitted_at).toLocaleString()
                : ""
            }
          />
        </dl>

        <div className="mt-8">
          <p className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
            Reviews
          </p>
          <VendorReviewsManager slug={app.slug} />
        </div>
      </div>

      {lightboxItem && (
        <VendorMediaLightbox
          item={lightboxItem}
          items={mediaUrls}
          onChange={setLightboxItem}
          onClose={() => setLightboxItem(null)}
        />
      )}
    </div>
  );
};

export default VendorDetailDrawer;
